import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { Xmark } from '@gravity-ui/icons';
import DistributionDonut from '../../../components/DistributionDonut/DistributionDonut';
import { countSelectedEvents, listViolationEvents } from '../../../services/violations';
import { countSelectedBusinessEvents } from '../../../services/businessAnalytics';
import { describeUserError } from '../../../utils/apiError';
import { formatDuration } from '../../../utils/formatDuration';
import {
  computeEventsStats,
  formatPercent,
  planCountRequests,
  selectionSignature,
} from './eventsStatsModel';
import { mergeCountResults } from './eventsQueryModel';
import { countRu, EVENT_FORMS } from '../../../utils/pluralRu';

/*
 * Панель «Посчитать» для выбранных событий.
 *
 * Распределения по правилам и метрикам считает backend (count-selected):
 * в режиме ids ему уходят event_ids, в режиме filters — снимок фильтра, так
 * что выбор «все по фильтру» считается без перечисления id и без лимита.
 *
 * Клиентский блок (длительность, точность по проверке, столбики по дням) —
 * только в режиме ids. Отмеченные события добираются постранично из списка
 * текущего фильтра, без потолка, с прогрессом. Событий вне текущего фильтра
 * список отдать не может, поэтому их в этом блоке нет, и это подписано.
 *
 * Результат и состояние панели (открыта/закрыта) лежат в extras выбора и
 * переживают Detail/Back. Результат привязан к подписи выбора: изменили выбор —
 * прежнее число относится к другому набору событий и не показывается.
 */
const STATS_PAGE_SIZE = 200;

const COUNTERS = { violation: countSelectedEvents, business: countSelectedBusinessEvents };

/*
 * Выбор из нарушений и бизнес-событий считается по частям — у каждой
 * категории свой count-selected, — а ответы складываются в один.
 */
const countPlan = (plan) => Promise.all(
  plan.requests.map(({ category, body }) => COUNTERS[category](body))
).then(mergeCountResults);

/** Листает список по фильтру, пока не найдёт все отмеченные события или не кончится список. */
const loadSelectedEvents = async (listEvents, baseParams, idSet, onProgress, isCancelled) => {
  const wanted = new Set([...idSet].map(Number));
  const found = [];
  let offset = 0;
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const response = await listEvents({ ...baseParams, limit: STATS_PAGE_SIZE, offset });
    if (isCancelled()) return null;
    response.items.forEach((event) => { if (wanted.has(Number(event.id))) found.push(event); });
    offset += STATS_PAGE_SIZE;
    onProgress({ scanned: Math.min(offset, response.total), total: response.total });
    if (response.items.length === 0 || offset >= response.total || found.length >= wanted.size) break;
  }
  return found;
};

const Kpi = ({ label, value, hint }) => (
  <div className="vio-stats-kpi">
    <span className="vio-stats-kpi-label">{label}</span>
    <span className="vio-stats-kpi-value">{value}</span>
    {hint && <span className="vio-stats-kpi-hint">{hint}</span>}
  </div>
);

const ClientStats = ({ stats, requested, found }) => (
  <>
    {found < requested && (
      <p className="vio-field-hint">
        {`Ниже учтены события текущего фильтра, ${found} из ${requested} отмеченных`}
      </p>
    )}
    <div className="vio-stats-kpis">
      <Kpi label="Средняя длительность" value={formatDuration(stats.durationAvg)} />
      <Kpi label="Суммарная длительность" value={formatDuration(stats.durationTotal)} />
      <Kpi
        label="Точность по проверке"
        value={formatPercent(stats.review.accuracy)}
        hint={stats.review.accuracy == null
          ? 'Нет событий с вердиктом «Подтверждено» или «Ошибка»'
          : `Подтверждено ${stats.review.confirmed}, ошибок ${stats.review.false_positive}`}
      />
      <Kpi
        label="Не проверено"
        value={stats.review.unreviewed}
        hint={stats.review.uncertain ? `${stats.review.uncertain} с вердиктом «Не уверен»` : undefined}
      />
      <Kpi
        label="Исключено из аналитики"
        value={stats.excluded}
        hint={stats.total ? formatPercent(stats.excluded / stats.total) : undefined}
      />
    </div>

    {stats.byDay.length > 0 && (
      <div className="vio-stats-chart">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={stats.byDay} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(value) => [value, 'Событий']} labelFormatter={(label) => `День ${label}`} />
            <Bar dataKey="count" fill="#2563eb" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    )}
  </>
);

const IDLE_CLIENT = { state: 'idle', events: [], progress: null };

const EventsSelectionStats = ({ selection, baseParams, listEvents = listViolationEvents }) => {
  const { mode, ids, types, filters, count, extras, setExtras } = selection;
  const signature = selectionSignature({ mode, ids, filters, count });
  const saved = extras?.stats || {};
  const open = Boolean(saved.open);
  const result = saved.result && saved.signature === signature ? saved.result : null;

  const [state, setState] = useState('idle');
  const [error, setError] = useState('');
  const [client, setClient] = useState(IDLE_CLIENT);
  const runRef = useRef(0);

  // Смена выбора и размонтирование отменяют незавершённую загрузку.
  useEffect(() => {
    setState('idle');
    setError('');
    setClient(IDLE_CLIENT);
    return () => { runRef.current += 1; };
  }, [signature]);

  // Клиентская догрузка стартует только после успешного count-selected и
  // останавливается, как только найдены все отмеченные события.
  const startClientLoad = (isCancelled) => {
    setClient({ state: 'loading', events: [], progress: null });
    loadSelectedEvents(listEvents, baseParams, ids, (progress) => {
      if (!isCancelled()) setClient((prev) => ({ ...prev, progress }));
    }, isCancelled)
      .then((events) => {
        if (events) setClient({ state: 'ready', events, progress: null });
      })
      .catch(() => {
        if (!isCancelled()) setClient({ state: 'error', events: [], progress: null });
      });
  };

  const plan = planCountRequests({ mode, ids, types, filters });

  const calculate = () => {
    if (!plan.available) return;
    runRef.current += 1;
    const run = runRef.current;
    const isCancelled = () => runRef.current !== run;

    setState('loading');
    setError('');
    setExtras({ stats: { open: true, signature, result: null } });

    countPlan(plan)
      .then((response) => {
        if (isCancelled()) return;
        setExtras({ stats: { open: true, signature, result: response } });
        setState('ready');
        if (mode === 'ids') startClientLoad(isCancelled);
      })
      .catch((countError) => {
        if (isCancelled()) return;
        setError(describeUserError(countError, 'Не удалось посчитать выбранные события'));
        setState('error');
      });
    setClient(IDLE_CLIENT);
  };

  const close = () => {
    runRef.current += 1;
    setState('idle');
    setClient(IDLE_CLIENT);
    setExtras({ stats: { open: false, signature: null, result: null } });
  };

  const clientStats = useMemo(() => computeEventsStats(client.events), [client.events]);

  if (count <= 0) return null;

  if (!open) {
    return (
      <section className="vio-card vio-stats" aria-label="Статистика по выборке">
        <div className="vio-stats-head">
          <button type="button" className="vio-btn vio-btn--primary" onClick={calculate} disabled={!plan.available}>
            Посчитать
          </button>
        </div>
        {plan.reason && <p className="vio-field-hint">{plan.reason}</p>}
      </section>
    );
  }

  const shownCount = result ? result.selected_count : count;

  return (
    <section className="vio-card vio-stats" aria-label="Статистика по выборке">
      <div className="vio-stats-head">
        <h3 className="vio-stats-title">{`Выбрано ${countRu(shownCount, EVENT_FORMS)}`}</h3>
        <span className="vio-stats-actions">
          <button
            type="button"
            className="vio-btn"
            onClick={calculate}
            disabled={state === 'loading' || !plan.available}
          >
            Пересчитать
          </button>
          <button
            type="button"
            className="vio-btn vio-btn--icon"
            onClick={close}
            title="Закрыть статистику"
            aria-label="Закрыть статистику"
          >
            <Xmark width={16} height={16} />
          </button>
        </span>
      </div>

      {plan.reason && <p className="vio-field-hint">{plan.reason}</p>}
      {state === 'loading' && <p className="vio-field-hint">Считаем…</p>}
      {state === 'error' && <p className="vio-field-hint vio-field-hint--error">{error}</p>}
      {open && !result && state === 'idle' && (
        <p className="vio-field-hint">Выбор изменился, нажмите «Пересчитать».</p>
      )}

      {result && (
        <div className="vio-stats-donuts">
          <DistributionDonut
            title="По названиям правил"
            distribution={result.rule_distribution}
            unitForms={EVENT_FORMS}
            emptyText="Нет данных"
          />
          <DistributionDonut
            title="По метрикам"
            distribution={result.metric_distribution}
            unitForms={EVENT_FORMS}
            emptyText="Нет данных"
          />
        </div>
      )}

      {mode === 'ids' && result && client.state === 'loading' && (
        <p className="vio-field-hint">
          {client.progress
            ? `Загружаем события… ${client.progress.scanned} из ${client.progress.total}`
            : 'Загружаем события…'}
        </p>
      )}
      {mode === 'ids' && result && client.state === 'error' && (
        <p className="vio-field-hint vio-field-hint--error">
          Не удалось загрузить события для длительности и точности
        </p>
      )}
      {mode === 'ids' && result && client.state === 'ready' && client.events.length === 0 && (
        <p className="vio-field-hint">Среди отмеченных нет событий текущего фильтра, длительность и точность не посчитаны.</p>
      )}
      {mode === 'ids' && result && client.state === 'ready' && client.events.length > 0 && (
        <ClientStats stats={clientStats} requested={ids.size} found={client.events.length} />
      )}
    </section>
  );
};

export default EventsSelectionStats;
